import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
} from "react-router-dom";
import { ReactNode } from "react";
import { getUser } from "../api-services/process";
import useAuth from "../custom-hooks/useAuth";
import Preloader from "../app-components/preloader/Preloader";
import { ELITELINKS, SITELINKS } from "./links";
import { Ban } from "lucide-react";

// main website pages/website/@
import WebsiteIndex from "../pages/website/WebsiteIndex";
import TermsOfUse from "../pages/website/TermsAndConditions";
import PrivacyPolicy from "../pages/website/PrivacyPolicy";

// authentication pages/auth/@
import Login from "../pages/auth/Login";
import RegisterStepOne from "../pages/auth/RegisterStepOne";
import RegisterStepTwo from "../pages/auth/RegisterStepTwo";
import RegisterStepThree from "../pages/auth/RegisterStepThree";
import UpdatePassword from "../pages/auth/UpdatePassword";
import VerifyEmail from "../pages/auth/VerifyEmail";
import ForgotPassword from "../pages/auth/ForgotPassword";
import VerificationPage from "../pages/auth/VerificationPage";
import GoogleCallback from "../pages/auth/GoogleCallback";

// hone-elite pages
import LinkedInCallback from "../pages/auth/LinkedInCallback";
import EliteHomePage from "../pages/hone_elite/home/Index";
import EliteMakeNewRequest from "../pages/hone_elite/request/MakeNewRequest";
import EliteSubscriptions from "../pages/hone_elite/subscription/Subscriptions";
import useEliteSubscriber from "../custom-hooks/useIsEliteSubscriber";
import WelcomeElite from "../pages/auth/WelcomeElite";
import EliteMyRequests from "../pages/hone_elite/request/MyRequests";
import MyEliteRequestMembers from "../pages/hone_elite/request/MyRequestmembers";
import MyEliteResponses from "../pages/hone_elite/request/MyResponses";
import EliteMessaging from "../pages/hone_elite/messaging/messaging";
import EliteNotifications from "../pages/hone_elite/notification/Notification";
import EliteSettings from "../pages/hone_elite/settings/Settings";
import MyElitePayments from "../pages/hone_elite/payment/MyPayments";
import EliteProfile from "../pages/hone_elite/profile/Profile";
import EditEliteProfile from "../pages/hone_elite/profile/EditProfile";
import ContactUs from "../pages/website/ContactUs";

// Middleware to validate token
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const user = getUser();

  // Show preloader while loading
  if (loading) {
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );
  }

  if (isAuthenticated) {
    if (!user?.username || !user?.DOB) {
      return <Navigate to={SITELINKS.REGISTER_STEP_TWO} replace />;
    }
  } else {
    localStorage.clear();
  }

  return isAuthenticated ? (
    <>
      <main className="sm:hidden block">{children}</main>
      <div className="sm:flex hidden items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-6 rounded-2xl shadow-lg flex flex-col items-center w-80 text-center">
          <Ban className="w-12 h-12 text-red-500" />
          <h2 className="text-lg font-semibold mt-4">
            Honneconnect Not Available
          </h2>
          <p className="text-gray-600 mt-2">
            This feature is not available on this device screen. Kindly use a
            mobile phone.
          </p>
          <Link to="/" className="text-blue-800 text-xs">
            Go Home
          </Link>
        </div>
      </div>
    </>
  ) : (
    <Navigate to={SITELINKS.LOGIN} replace />
  );
}

// ProtectedAuthRoute
function ProtectedAuthRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  // Show preloader while loading
  if (loading) {
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );
  }

  return isAuthenticated ? (
    <>
      <main className="sm:hidden block">{children}</main>
      <div className="sm:flex hidden items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-6 rounded-2xl shadow-lg flex flex-col items-center w-80 text-center">
          <Ban className="w-12 h-12 text-red-500" />
          <h2 className="text-lg font-semibold mt-4">
            Honneconnect Not Available
          </h2>
          <p className="text-gray-600 mt-2">
            This feature is not available on this device screen. Kindly use a
            mobile phone.
          </p>
          <Link to="/" className="text-blue-800 text-xs">
            Go Home
          </Link>
        </div>
      </div>
    </>
  ) : (
    <Navigate to={SITELINKS.LOGIN} replace />
  );
}

function EliteSubscription({ children }: { children: ReactNode }) {
  const { isSubscribed, loading } = useEliteSubscriber();

  if (loading) {
    return (
      <div>
        <Preloader loading={loading} />
      </div>
    );
  }

  if (!isSubscribed) {
    return <Navigate to={ELITELINKS.SUBSCRIPTIONS} replace />;
  }

  return <>{children}</>;
}

// config routes
function APPRoutes() {
  return (
    <Router>
      <Routes>
        {/* public routes */}
        {/* <Route path={'/'} element={<Login />} /> */}
        <Route path="/" element={<WebsiteIndex />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/terms-and-conditions" element={<TermsOfUse />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/verify/:email/:code" element={<VerifyEmail />} />

        {/* app authentication pages */}
        <Route path={SITELINKS.LOGIN} element={<Login />} />
        <Route path={SITELINKS.GOOGLE_LOGIN} element={<GoogleCallback />} />
        <Route path={SITELINKS.LINKEDIN_LOGIN} element={<LinkedInCallback />} />

        <Route path={SITELINKS.REGISTER} element={<RegisterStepOne />} />
        <Route path={SITELINKS.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route path={SITELINKS.VERIFICATION} element={<VerificationPage />} />

        {/* protected auth area */}
        <Route
          path={SITELINKS.REGISTER_STEP_TWO}
          element={
            <ProtectedAuthRoute>
              <RegisterStepTwo />
            </ProtectedAuthRoute>
          }
        />
        <Route
          path={SITELINKS.REGISTER_STEP_THREE}
          element={
            <ProtectedAuthRoute>
              <RegisterStepThree />
            </ProtectedAuthRoute>
          }
        />
        <Route
          path={ELITELINKS.ELITE}
          element={
            <ProtectedAuthRoute>
              <WelcomeElite />
            </ProtectedAuthRoute>
          }
        />
        <Route
          path={SITELINKS.UPDATE_PASSWORD}
          element={
            <ProtectedAuthRoute>
              <UpdatePassword />
            </ProtectedAuthRoute>
          }
        />

        {/* Hone-elite pages */}
        <Route
          path={SITELINKS.HOME}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <EliteHomePage />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />

        <Route
          path={ELITELINKS.SUBSCRIPTIONS}
          element={
            <ProtectedRoute>
              <EliteSubscriptions />
            </ProtectedRoute>
          }
        />
        <Route
          path={'/flutterwave-callback'}
          element={
              <EliteSubscriptions />
          }
        />
        <Route
          path={`${ELITELINKS.SUBSCRIPTIONS}/:id`}
          element={
            <ProtectedRoute>
              <EliteSubscriptions />
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.HOME}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <EliteHomePage />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.MAKE_REQUEST_AUTOMATIC}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <EliteMakeNewRequest />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.MAKE_REQUEST}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <EliteMakeNewRequest />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.MY_REQUESTS}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <EliteMyRequests />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={`${ELITELINKS.REQUEST_HISTORY}/:id`}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <MyEliteRequestMembers />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.MY_RESPONSES}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <MyEliteResponses />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.CHAT}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <EliteMessaging />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={`${ELITELINKS.CHATIN}`}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <EliteMessaging />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.NOTIFICATIONS}
          element={
            <ProtectedRoute>
                <EliteNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.SETTINGS}
          element={
            <ProtectedRoute>
                <EliteSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.MY_PAYMENTS}
          element={
            <ProtectedRoute>
                <MyElitePayments />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${ELITELINKS.MY_PROFILE}/:id`}
          element={
            <ProtectedRoute>
              <EliteSubscription>
                <EliteProfile />
              </EliteSubscription>
            </ProtectedRoute>
          }
        />
        <Route
          path={ELITELINKS.EDIT_PROFILE}
          element={
            <ProtectedRoute>
                <EditEliteProfile />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default APPRoutes;
